import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useErrorBoundary, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { GraphQLErrorMessage, Modal, Paragraph, Popover, useToasts } from '@treams/treams-ui';

export default function BulkModal(
	props: PropsWithChildren<{
		name: string;
		valid?: boolean;
		users: graphql.User[];
		apply: (user: graphql.User) => Promise<number | void>;
		applySingle?: (users: graphql.User[]) => Promise<void>;
		onCreate?: () => void;
		onDismiss: () => void;
	}>,
) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const { addToast } = useToasts();

	const [error, attempt] = useErrorBoundary();

	const [loading, setLoading] = useState(false);
	const [progress, setProgress] = useState(0.0);
	const [affected, setAffected] = useState(0);

	const cycles = graphql.useCycleTemplatesQuery({
		variables: {
			isArchived: false,
			first: 100,
		},
	});

	const options = cycles.data?.cycleTemplates.edges.map(({ node }) => ({
		label: localize(node.title).text,
		value: node.id,
	}));
	options?.sort((a, b) => a.label.localeCompare(b.label));

	const onCreate = attempt(async () => {
		if (props.valid === false || loading) {
			return;
		}

		setLoading(true);

		await props.applySingle?.(props.users);

		for (const [index, user] of props.users.entries()) {
			const result = (await props.apply(user)) as number;

			if (result !== undefined) {
				setAffected((affected) => affected + result);
			}

			setProgress((index + 1) / props.users.length);
		}

		setLoading(false);

		addToast(t(`employees.${props.name}.success`), {
			appearance: 'success',
			autoDismiss: true,
		});
		props.onDismiss();
	});

	return (
		<Popover visible center>
			<Modal>
				<Modal.Header title={t(`employees.${props.name}.title`)} onClose={props.onDismiss} />
				<Modal.Contents>
					{error && <GraphQLErrorMessage error={error} />}

					{props.children}

					<Flex flexDirection="row" justifyContent="space-between" alignItems="center">
						<Box mr={2} width={7 / 12}>
							{loading && (
								<Paragraph>
									{t(`employees.${props.name}.loading.instruction`, {
										progress: (progress * 100).toFixed(0),
										count: affected,
									})}
								</Paragraph>
							)}
						</Box>
					</Flex>
				</Modal.Contents>
				<Modal.FormFooter
					onCancel={props.onDismiss}
					onSave={onCreate}
					disabled={props.valid === false}
					saveTitle={t(`employees.${props.name}.apply`)}
				/>
			</Modal>
		</Popover>
	);
}
